import { message } from "antd";
import { api } from "../api";
import {
  WhatsappTemplateButtonTypeEnum,
  WhatsappTemplateCategoryEnum,
} from "../enums";
import formChecks from "../functions/templateFormVerification";

export async function submitTemplate({
  isInPanel,
  templateName,
  body,
  cards,
  footer,
  flowButton,
  copyCodeButton,
  urlButton,
  urlButtonLink,
  urlButton2,
  urlButtonLink2,
  urlButtonTrackingEnabled,
  urlButton2TrackingEnabled,
  quickReply1,
  quickReply2,
  quickReply3,
  numberOfQuickReplies,
  numberOfUrl,
  replyButtons,
  isUrlButtonAtBottom,
  selectedHeader,
  selectedCategory,
  user,
  setIsSubmitLoading,
  toggleActiveSumbit,
  setProgression,
  setNewTemplateName,
  limitedTimeOffer,
  errorSetters,
}) {
  console.info("[submitTemplate] Initialisation - urlButton2:", urlButton2);

  if (errorSetters) {
    errorSetters.setTemplateNameError?.("");
    errorSetters.setBodyErrorMsg?.("");
    errorSetters.setQuickReply1ErrorMsg?.("");
    errorSetters.setQuickReply2ErrorMsg?.("");
    errorSetters.setQuickReply3ErrorMsg?.("");
    errorSetters.setUrlButtonErrorMsg?.("");
    errorSetters.setUrlButtonLinkErrorMsg?.("");
    errorSetters.setUrlButton2ErrorMsg?.("");
    errorSetters.setUrlButtonLink2ErrorMsg?.("");
    errorSetters.setLimitedTimeOfferTextError?.("");
    errorSetters.setCustomField1ErrorMsg?.("");
    errorSetters.setCustomField2ErrorMsg?.("");
    errorSetters.setCustomField3ErrorMsg?.("");
    errorSetters.setBodyCardErrors?.([]);
    errorSetters.setButtonCardErrors?.([]);
  }

  const templateErrorMessage =
    "Template could not be submitted due to errors, please fix it before submitting again";

  const delay = (delayInms) => {
    return new Promise((resolve) => setTimeout(resolve, delayInms));
  };

  const cleanTemplateName = (name) =>
    removeEmojisFromText(
      name
        .toLowerCase()
        .replaceAll(" ", "_")
        .replaceAll("-", "_")
        .replaceAll("%", "")
        .replaceAll(",", "")
        .replaceAll(";", "")
        .replaceAll("!", "")
        .replaceAll("?", "")
        .replaceAll("[", "")
        .replaceAll("]", "")
        .replaceAll("'", "")
        .replaceAll(".", "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""),
    );

  const removeEmojisFromText = (text) =>
    text.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      "",
    );

  let hasError = false;

  const templateNameError = formChecks.checkTemplateName(templateName);
  errorSetters?.setTemplateNameError?.(templateNameError);
  if (templateNameError) hasError = true;

  const bodyError = formChecks.checkBody(body);
  errorSetters?.setBodyErrorMsg?.(bodyError);
  if (bodyError) hasError = true;

  if (numberOfUrl >= 1) {
    const urlButtonError = !urlButton
      ? "Title is required"
      : formChecks.checkUrlButton(urlButton, urlButtonLink);
    errorSetters?.setUrlButtonErrorMsg?.(urlButtonError);
    if (urlButtonError) hasError = true;

    const urlButtonLinkError = !urlButtonLink
      ? "Link is required"
      : formChecks.checkUrlButtonLink(false, urlButtonLink, urlButton);
    errorSetters?.setUrlButtonLinkErrorMsg?.(urlButtonLinkError);
    if (urlButtonLinkError) hasError = true;
  } else {
    errorSetters?.setUrlButtonErrorMsg?.("");
    errorSetters?.setUrlButtonLinkErrorMsg?.("");
  }

  if (numberOfUrl >= 2) {
    const urlButton2Error = !urlButton2
      ? "Title is required"
      : formChecks.checkUrlButton(urlButton2, urlButtonLink2);
    errorSetters?.setUrlButton2ErrorMsg?.(urlButton2Error);
    if (urlButton2Error) hasError = true;

    const urlButtonLink2Error = !urlButtonLink2
      ? "Link is required"
      : formChecks.checkUrlButtonLink(false, urlButtonLink2, urlButton2);
    errorSetters?.setUrlButtonLink2ErrorMsg?.(urlButtonLink2Error);
    if (urlButtonLink2Error) hasError = true;
  } else {
    errorSetters?.setUrlButton2ErrorMsg?.("");
    errorSetters?.setUrlButtonLink2ErrorMsg?.("");
  }

  if (numberOfQuickReplies >= 1) {
    const quickReply1Error = formChecks.checkQuickReply(quickReply1);
    errorSetters?.setQuickReply1ErrorMsg?.(quickReply1Error);
    if (quickReply1Error) hasError = true;
  }

  if (numberOfQuickReplies >= 2) {
    const quickReply2Error = formChecks.checkQuickReply(quickReply2);
    errorSetters?.setQuickReply2ErrorMsg?.(quickReply2Error);
    if (quickReply2Error) hasError = true;
  }

  if (numberOfQuickReplies >= 3) {
    const quickReply3Error = formChecks.checkQuickReply(quickReply3);
    errorSetters?.setQuickReply3ErrorMsg?.(quickReply3Error);
    if (quickReply3Error) hasError = true;
  }

  // A quick reply button set to "save click as custom field" must have a
  // custom field selected, same requirement as the onBlur check in the form.
  if (numberOfQuickReplies >= 1) {
    const customField1Error = formChecks.checkCustomField(
      replyButtons?.[0]?.saveAsCustomField,
      replyButtons?.[0]?.customField,
    );
    errorSetters?.setCustomField1ErrorMsg?.(customField1Error);
    if (customField1Error) hasError = true;
  }

  if (numberOfQuickReplies >= 2) {
    const customField2Error = formChecks.checkCustomField(
      replyButtons?.[1]?.saveAsCustomField,
      replyButtons?.[1]?.customField,
    );
    errorSetters?.setCustomField2ErrorMsg?.(customField2Error);
    if (customField2Error) hasError = true;
  }

  if (numberOfQuickReplies >= 3) {
    const customField3Error = formChecks.checkCustomField(
      replyButtons?.[2]?.saveAsCustomField,
      replyButtons?.[2]?.customField,
    );
    errorSetters?.setCustomField3ErrorMsg?.(customField3Error);
    if (customField3Error) hasError = true;
  }

  if (selectedCategory === WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER) {
    const ltoLinkError = formChecks.checkUrlButtonLink(
      true,
      urlButtonLink,
      urlButton,
    );
    errorSetters?.setUrlButtonLinkErrorMsg?.(ltoLinkError);
    if (ltoLinkError) hasError = true;

    const ltoTextError = formChecks.checkLimitedTimeOfferText(limitedTimeOffer);
    errorSetters?.setLimitedTimeOfferTextError?.(ltoTextError);
    if (ltoTextError) hasError = true;
  }

  if (selectedCategory === WhatsappTemplateCategoryEnum.CAROUSEL) {
    const carouselResult = formChecks.checkCarousel(cards);
    if (carouselResult.hasError || carouselResult.generalError) {
      errorSetters?.setBodyCardErrors?.(carouselResult.bodyCardErrors);
      errorSetters?.setButtonCardErrors?.(carouselResult.buttonCardErrors);
      hasError = true;
    }
  }

  if (hasError) {
    message.error(templateErrorMessage);
    return { success: false };
  }

  if (isInPanel) {
    setIsSubmitLoading(true);
  }

  const globalVariables = await api.globalVariable.findMany({
    filter: {
      OR: [
        {
          key: { equals: "imageHandle" },
        },
        {
          key: { equals: "videoHandle" },
        },
      ],
    },
    select: {
      id: true,
      key: true,
      stringValue: true,
    },
  });

  const waPublicLinkImage = globalVariables.find(
    (variable) => variable.key === "imageHandle",
  )?.stringValue;
  const waPublicLinkVideo = globalVariables.find(
    (variable) => variable.key === "videoHandle",
  )?.stringValue;
  const isCloudApi =
    user?.customer?.waAccessToken || user?.customer?.codeForAlcmeon;

  var submittedTemplate = {
    name: cleanTemplateName(templateName),
    language: "fr",
    category:
      selectedCategory === WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER ||
      selectedCategory === WhatsappTemplateCategoryEnum.CAROUSEL
        ? WhatsappTemplateCategoryEnum.MARKETING
        : (selectedCategory ?? WhatsappTemplateCategoryEnum.UTILITY),
    components: [],
  };

  // Ajout de l'en-tête en fonction de la sélection
  if (selectedHeader[0] === "image") {
    const imageHandle = isCloudApi
      ? waPublicLinkImage
      : "https://storage.gadget.dev/files/22129/42940/media/file/SuDoCyhFJkwqnGPOm30Of/5dd9eda2-817f-40c4-84d2-f5674654ec97";
    submittedTemplate.components.push({
      type: "HEADER",
      format: "IMAGE",
      example: {
        header_handle: [imageHandle],
      },
    });
  } else if (selectedHeader[0] === "video") {
    const videoHandle = isCloudApi
      ? waPublicLinkVideo
      : "https://storage.gadget.dev/files/22129/42940/media/file/xDycluQ_j76bA150Asp7G/2bc118d2-9e93-405c-9b5e-dced1b1051c0";

    submittedTemplate.components.push({
      type: "HEADER",
      format: "VIDEO",
      example: {
        header_handle: [videoHandle],
      },
    });
  }

  // Ajout du corps
  if (body !== "") {
    var component = {
      type: "BODY",
      text: body,
    };
    let k = 1;
    while (body.includes(`{{${k}}}`)) {
      if (k === 1) {
        component.example = {
          body_text: [["Emilia"]],
        };
      } else {
        component.example.body_text[0].push("Marie");
      }
      k++;
    }
    submittedTemplate.components.push(component);
  }

  let officialUrlButtons = [];
  if (cards) {
    let imageHeader, videoHeader;

    // Récupération du premier composant de type CAROUSEL
    console.info("[submitTemplate] Carousel processing - cards:", cards);
    const firstCarouselComponent = cards[0]?.components.find(
      (c) => c.type === "HEADER",
    );
    console.info(
      "[submitTemplate] Carousel processing - first card:",
      cards[0],
    );
    console.info(
      "[submitTemplate] Carousel processing - first carousel component:",
      firstCarouselComponent,
    );

    if (firstCarouselComponent?.format.toLowerCase() === "image") {
      imageHeader = {
        type: "HEADER",
        format: "IMAGE",
        example: {
          header_handle: [
            isCloudApi
              ? waPublicLinkImage
              : "https://storage.gadget.dev/files/22129/42940/media/file/SuDoCyhFJkwqnGPOm30Of/5dd9eda2-817f-40c4-84d2-f5674654ec97",
          ],
        },
      };
    } else {
      videoHeader = {
        type: "HEADER",
        format: "VIDEO",
        example: {
          header_handle: [
            isCloudApi
              ? waPublicLinkVideo
              : "https://storage.gadget.dev/files/22129/42940/media/file/xDycluQ_j76bA150Asp7G/2bc118d2-9e93-405c-9b5e-dced1b1051c0",
          ],
        },
      };
    }

    // Ajout des exemples dans chaque carte
    const updatedCards = cards.map((card) => {
      const bodyComponent = card.components.find((c) => c.type === "BODY");
      const bodyText = bodyComponent ? bodyComponent.text : "";

      const buttonsComponent = card.components.find(
        (c) => c.type === "BUTTONS",
      );

      // console.log(buttonsComponent);
      const urlButtons =
        buttonsComponent?.buttons.filter(
          (c) => c.type === "URL" || c.type === "url",
        ) || [];
      officialUrlButtons.push(urlButtons);

      // console.log(imageHeader);
      const updatedCard = {
        components: [
          firstCarouselComponent?.format.toLowerCase() === "image"
            ? imageHeader
            : videoHeader,
          {
            type: "BODY",
            text: bodyText,
          },
          {
            type: "BUTTONS",
            buttons: [],
          },
        ],
      };

      // SP-8442 FIXME should create a redirection record to track the clicks on the buttons.
      // ici on cree des redirection en mode example. au moment de l envoie, on doit creer des redirection reelles.
      buttonsComponent?.buttons.forEach((button) => {
        if (button.type === "URL" || button.type === "url") {
          const updatedUrlButton = button.url.includes("{{1}}")
            ? {
                type: "URL",
                text: button.text,
                url: button.url,
                example: [button.url + "126T783173Y9121B9Q"],
              }
            : {
                type: "URL",
                text: button.text,
                url: `https://${
                  window.gadgetConfig.environment === "production"
                    ? "app.getsimio.com"
                    : `getsimio--${window.gadgetConfig.environment}.gadget.app`
                }/redirect/{{1}}`,
                example: [
                  `https://${
                    window.gadgetConfig.environment === "production"
                      ? "app.getsimio.com"
                      : `getsimio--${window.gadgetConfig.environment}.gadget.app`
                  }/redirect/126T783173Y9121B9Q`,
                ],
              };
          updatedCard.components[2].buttons.push(updatedUrlButton);
        } else if (
          button.type === "QUICK_REPLY" ||
          button.type === "quick_reply"
        ) {
          updatedCard.components[2].buttons.push({
            type: "QUICK_REPLY",
            text: button.text,
          });
        }
      });

      // Ajouter des exemples pour les variables dans le texte du corps
      let k = 1;
      while (bodyText.includes(`{{${k}}}`)) {
        if (k === 1) {
          updatedCard.components[1].example = {
            body_text: [["Emilia"]],
          };
        } else {
          updatedCard.components[1].example.body_text[0].push("Marie");
        }
        k++;
      }

      return updatedCard;
    });

    // Ajout au template final
    const carousel = {
      type: "CAROUSEL",
      cards: updatedCards,
    };
    submittedTemplate.components.push(carousel);
  } else {
    // Ajout du pied de page
    if (footer !== "") {
      submittedTemplate.components.push({
        type: "FOOTER",
        text: footer,
      });
    }

    // Ajout des boutons
    if (
      flowButton ||
      urlButton !== "" ||
      quickReply1 !== "" ||
      quickReply2 !== "" ||
      quickReply3 !== ""
    ) {
      var buttons = {
        type: "BUTTONS",
        buttons: [],
      };

      if (flowButton) {
        const { flowId, flowAction, navigateScreen, text } = flowButton;
        buttons.buttons.push({
          flow_id: flowId,
          flow_action: flowAction,
          navigate_screen: navigateScreen,
          text,
          type: WhatsappTemplateButtonTypeEnum.FLOW,
        });
      }
      if (quickReply1 !== "") {
        buttons.buttons.push({
          type: WhatsappTemplateButtonTypeEnum.QUICK_REPLY,
          text: quickReply1,
        });
      }
      if (quickReply2 !== "") {
        buttons.buttons.push({
          type: WhatsappTemplateButtonTypeEnum.QUICK_REPLY,
          text: quickReply2,
        });
      }
      if (quickReply3 !== "") {
        buttons.buttons.push({
          type: WhatsappTemplateButtonTypeEnum.QUICK_REPLY,
          text: quickReply3,
        });
      }
      if (urlButton !== "") {
        const urlButtonToPush =
          urlButtonLink?.includes("{{1}}") ||
          (urlButtonTrackingEnabled ?? true) === false
            ? {
                type: WhatsappTemplateButtonTypeEnum.URL,
                text: urlButton,
                url: urlButtonLink,
                example: [urlButtonLink + "126T783173Y9121B9Q"],
              }
            : {
                type: WhatsappTemplateButtonTypeEnum.URL,
                text: urlButton,
                url: `https://${
                  window.gadgetConfig.environment === "production"
                    ? "app.getsimio.com"
                    : `getsimio--${window.gadgetConfig.environment}.gadget.app`
                }/redirect/{{1}}`,
                example: [
                  `https://${
                    window.gadgetConfig.environment === "production"
                      ? "app.getsimio.com"
                      : `getsimio--${window.gadgetConfig.environment}.gadget.app`
                  }/redirect/126T783173Y9121B9Q`,
                ],
              };

        // Consider to reorder buttons except for LTO where the order is hardcoded (COPY_CODE > URL > QUICK_REPLY).
        if (!limitedTimeOffer || !limitedTimeOffer.text) {
          if (isUrlButtonAtBottom) {
            buttons.buttons.push(urlButtonToPush);
          } else {
            buttons.buttons.unshift(urlButtonToPush);
          }
        } else {
          buttons.buttons.unshift(urlButtonToPush);

          if (copyCodeButton) {
            const { text } = copyCodeButton;
            const _copyCodeButton = {
              type: WhatsappTemplateButtonTypeEnum.COPY_CODE,
              example: [text],
            };
            buttons.buttons.unshift(_copyCodeButton);
          }
        }
      }
      console.info("[submitTemplate] Buttons configuration:", buttons);

      submittedTemplate.components.push(buttons);
    }

    if (limitedTimeOffer?.text) {
      const { text } = limitedTimeOffer;
      submittedTemplate.components.push({
        type: "LIMITED_TIME_OFFER",
        limited_time_offer: { text, has_expiration: true },
      });
    }

    if (urlButton2 && urlButton2 !== "") {
      const urlButton2ToPush =
        urlButtonLink2?.includes("{{1}}") ||
        (urlButton2TrackingEnabled ?? true) === false
          ? {
              type: WhatsappTemplateButtonTypeEnum.URL,
              text: urlButton2,
              url: urlButtonLink2,
              example: [urlButtonLink2 + "126T783173Y9121B9Q"],
            }
          : {
              type: WhatsappTemplateButtonTypeEnum.URL,
              text: urlButton2,
              url: `https://${
                window.gadgetConfig.environment === "production"
                  ? "app.getsimio.com"
                  : `getsimio--${window.gadgetConfig.environment}.gadget.app`
              }/redirect/{{1}}`,
              example: [
                `https://${
                  window.gadgetConfig.environment === "production"
                    ? "app.getsimio.com"
                    : `getsimio--${window.gadgetConfig.environment}.gadget.app`
                }/redirect/126T783173Y9121B9Q`,
              ],
            };

      // Consider to reorder buttons except for LTO where the order is hardcoded (COPY_CODE > URL > QUICK_REPLY).
      if (!limitedTimeOffer || !limitedTimeOffer.text) {
        if (isUrlButtonAtBottom) {
          buttons.buttons.push(urlButton2ToPush);
        } else {
          buttons.buttons.splice(1, 0, urlButton2ToPush);
        }
      } else {
        buttons.buttons.splice(1, 0, urlButton2ToPush);

        if (copyCodeButton) {
          const { text } = copyCodeButton;
          const _copyCodeButton = {
            type: WhatsappTemplateButtonTypeEnum.COPY_CODE,
            example: [text],
          };
          buttons.buttons.unshift(_copyCodeButton);
        }
      }
    }

    console.info(
      "[submitTemplate] Buttons configuration (urlButton2):",
      buttons,
    );
  }

  console.info("[submitTemplate] Final template structure:", submittedTemplate);

  if (!isInPanel) {
    setProgression(0);
  }

  const response = await api.sumbitTemplateTo360Dialog({
    template: JSON.stringify(submittedTemplate),
    customerId: user.customerId,
    buttonUrlLink: urlButtonLink,
    buttonUrlLink2: urlButtonLink2,
    shouldUseRedirection:
      !urlButtonLink?.includes("{{1}}") &&
      (urlButtonTrackingEnabled ?? true) !== false,
    shouldUseRedirection2:
      !urlButtonLink2?.includes("{{1}}") &&
      (urlButton2TrackingEnabled ?? true) !== false,
    trackingEnabled: (urlButtonTrackingEnabled ?? true) !== false,
    trackingEnabled2: (urlButton2TrackingEnabled ?? true) !== false,
    officialUrlButtons: JSON.stringify(officialUrlButtons),
  });

  console.info("[submitTemplate] API response:", response);
  if (response?.success) {
    setNewTemplateName(response.templateName);
    toggleActiveSumbit();

    if (!isInPanel) {
      await delay(1200);
      for (let k = 0; k < 14; k++) {
        await delay(1500 + (-1) ** k * k * 100);
        setProgression((n) => n + 5);
      }
      await delay(1200);
      setProgression(83);
      await delay(900);
      setProgression(99);
      await delay(1000);
      setProgression(100);
    }
  } else {
    return {
      success: false,
      error: response.message || "Failed to submit template",
    };
  }

  if (isInPanel) {
    setIsSubmitLoading(false);
  }
  return { success: true, templateName: response.templateName };
}
